// Credits API — wraps the FlowUp credit-billing endpoints.
import { api } from "./api";

export const CREDIT_TO_NIS = 0.6;
export const AI_COST_MULTIPLIER = 3;

export const TOPUP_METHODS = [
  { key: "credit_card", label: "כרטיס אשראי", icon: "credit-card" },
  { key: "paypal", label: "PayPal", icon: "wallet" },
  { key: "bank_transfer", label: "העברה בנקאית", icon: "landmark" },
  { key: "bit", label: "ביט", icon: "smartphone" },
  { key: "paybox", label: "PayBox", icon: "smartphone" },
];

export const ADDONS_META = [
  { key: "whatsapp_automation", label: "אוטומציית וואטסאפ" },
  { key: "email_automation", label: "אוטומציית מיילים" },
  { key: "sms_automation", label: "אוטומציית SMS" },
  { key: "ai_whatsapp_bot", label: "בוט AI חכם" },
];

export async function getCredits(companyId) {
  const { data } = await api.get(`/companies/${companyId}/credits`);
  return data;
}

export async function listCreditOperations(companyId, limit = 100) {
  const { data } = await api.get(`/companies/${companyId}/credits/operations`, { params: { limit } });
  return data.operations || [];
}

export async function listCreditTopups(companyId, limit = 100) {
  const { data } = await api.get(`/companies/${companyId}/credits/topups`, { params: { limit } });
  return data.topups || [];
}

export async function requestTopup(companyId, payload) {
  // payload: { credits, method, note }
  const { data } = await api.post(`/companies/${companyId}/credits/topup-request`, payload);
  return data;
}

// ===== Super-admin =====
export async function logAiCost(companyId, payload) {
  // payload: { raw_credits, description, session_start_balance?, session_end_balance? }
  const { data } = await api.post(`/admin/companies/${companyId}/log-ai-cost`, payload);
  return data;
}

export async function adminTopup(companyId, payload) {
  const { data } = await api.post(`/admin/companies/${companyId}/topup`, payload);
  return data;
}

// Owner spec 2026-06-15g: full credit reset — wipes top-ups,
// operations and AI sessions and seeds a fresh purchase row.
export async function adminResetCredits(companyId, payload) {
  // payload: { credits, price_ils, note }
  const { data } = await api.post(`/admin/companies/${companyId}/credits/reset`, payload);
  return data;
}

export async function listPendingTopups(status = "pending") {
  const { data } = await api.get(`/admin/topup-requests`, { params: { status } });
  return data.topups || [];
}

export async function approveTopup(topupId) {
  const { data } = await api.post(`/admin/topup-requests/${topupId}/approve`);
  return data;
}

export async function rejectTopup(topupId) {
  const { data } = await api.post(`/admin/topup-requests/${topupId}/reject`);
  return data;
}

export async function adjustCredits(companyId, payload) {
  // payload: { delta_credits, reason }
  const { data } = await api.post(`/admin/companies/${companyId}/credit-adjust`, payload);
  return data;
}

export async function setAddons(companyId, addons) {
  const { data } = await api.patch(`/admin/companies/${companyId}/addons`, { addons });
  return data;
}

// ===== Work Session =====
export async function startWorkSession(companyId, startCredits) {
  const { data } = await api.post(`/admin/companies/${companyId}/work-session/start`, {
    start_credits: Number(startCredits),
  });
  return data;
}

export async function endWorkSession(companyId, endCredits, description = "", multiplierOverride = null) {
  const { data } = await api.post(`/admin/companies/${companyId}/work-session/end`, {
    end_credits: Number(endCredits),
    description,
    // Only send when the SA explicitly chose 1× or 2×; null/3 fall back
    // to the default platform-wide AI_COST_MULTIPLIER (3) on the backend.
    multiplier_override: multiplierOverride === 1 || multiplierOverride === 2 ? multiplierOverride : null,
  });
  return data;
}

export async function cancelWorkSession(companyId) {
  const { data } = await api.post(`/admin/companies/${companyId}/work-session/cancel`);
  return data;
}

// ===== Focus flag (AI agent target marker) =====
// Marking a company "focused" tells the AI agent and the platform that
// any subsequent session / deploy targets ONLY that company.
export async function getFocusedCompany() {
  const { data } = await api.get(`/admin/focus`);
  return data.focused || null;
}

export async function setFocusCompany(companyId) {
  const { data } = await api.post(`/admin/companies/${companyId}/focus`);
  return data;
}

export async function clearFocusCompany(companyId) {
  const { data } = await api.delete(`/admin/companies/${companyId}/focus`);
  return data;
}

export async function getActiveSession(companyId) {
  const { data } = await api.get(`/companies/${companyId}/credits/active-session`);
  return data.active_session || null;
}

export async function addSessionActivity(companyId, summary) {
  const { data } = await api.post(
    `/admin/companies/${companyId}/work-session/activity`,
    { summary }
  );
  return data;
}

export async function listSessionActivities(companyId) {
  const { data } = await api.get(`/admin/companies/${companyId}/work-session/activities`);
  return data.activities || [];
}

export function formatCredits(n) {
  const v = Number(n) || 0;
  return new Intl.NumberFormat("he-IL", { maximumFractionDigits: 2 }).format(v);
}

export function formatNis(n) {
  // Show 2 decimals for small amounts (< 100 ₪) so the SA sees the
  // exact charge — e.g. 1 credit × ×2 multiplier = 1.20 ₪, not "1 ₪".
  // For larger amounts we still round to integer for visual cleanliness.
  const v = Number(n) || 0;
  const fractionDigits = Math.abs(v) < 100 ? 2 : 0;
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits === 2 ? 2 : 0,
  }).format(v);
}

export function creditsToNis(c) {
  return Number(c || 0) * CREDIT_TO_NIS;
}
